// src/pages/user/ProfilePage.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useModal } from '@/contexts/ModalContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchMyStats,
  fetchMySubmissions,
  updateMe,
  resendVerification,
} from '@/services/apiService';
import { EmailStatusBadge } from '@/components/ui/EmailStatusBadge';
import { ChangeEmailForm } from '@/components/forms/ChangeEmailForm';

const validateDisplayName = (name) => {
  if (!name || !name.trim()) return null;
  const cleaned = name.trim();
  if (cleaned.length > 50) return "Display name must be 50 characters or less.";
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length > 2) return "Display name can be at most 2 words (e.g., 'Larry Jackson').";
  if (words.length === 0) return "Display name must contain at least one word.";
  return null;
};

function classNames(...xs) {
  return xs.filter(Boolean).join(' ');
}

const Avatar = ({ src, name, size = 96 }) => {
  const initials = (name || '')
    .split(' ')
    .map((x) => x[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return src ? (
    <img
      src={src}
      alt="avatar"
      className="rounded-full object-cover border border-zinc-800"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      className="rounded-full bg-zinc-800 text-zinc-200 grid place-items-center border border-zinc-700"
      style={{ width: size, height: size }}
    >
      <span className="text-xl font-semibold">{initials || 'U'}</span>
    </div>
  );
};

export default function ProfilePage() {
  const { user, refreshMe, handleLogout } = useAuth();
  const { openModal } = useModal();

  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [bioInput, setBioInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [displayNameError, setDisplayNameError] = useState(null);

  const [disciplineInput, setDisciplineInput] = useState('');
  const [disciplineOtherInput, setDisciplineOtherInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [openToInput, setOpenToInput] = useState([]);

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  const displayName = useMemo(() => {
    return user?.profile?.name || user?.first_name || user?.username || 'User';
  }, [user]);

  const toMimeType = (fileType) => {
    if (fileType === 'image') return 'image/jpeg';
    if (fileType === 'video') return 'video/mp4';
    return 'application/octet-stream';
  };

  const { files, fileToSubmissionMap } = useMemo(() => {
    const fileList = [];
    const map = {};
    subs.forEach((s) => {
      (s.files || []).forEach((f) => {
        if (f.upload_status === 'confirmed') {
          const fileId = `${s.id}-${f.id}`;
          fileList.push({
            id: fileId,
            url: f.file_url,
            fileType: f.file_type,
            mime: toMimeType(f.file_type),
          });
          map[fileId] = s;
        }
      });
    });
    return { files: fileList, fileToSubmissionMap: map };
  }, [subs]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const me = await refreshMe();
        if (mounted && me) {
          setNameInput(me?.profile?.name || '');
          setBioInput(me?.profile?.bio || '');
          setDisciplineInput(me?.profile?.discipline || '');
          setDisciplineOtherInput(me?.profile?.discipline_other || '');
          setLocationInput(me?.profile?.location || '');
          setOpenToInput(me?.profile?.open_to || []);
        }
       

        const [st, submissions] = await Promise.all([
          fetchMyStats().catch(() => null),
          fetchMySubmissions().catch(() => []),
        ]);
        if (!mounted) return;
        setStats(st);
        setSubs(submissions);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [refreshMe]);

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setNameInput(newName);
    setDisplayNameError(validateDisplayName(newName));
  };

  const onSaveProfile = async () => {
    const nameError = validateDisplayName(nameInput);
    if (nameError) { setDisplayNameError(nameError); return; }
    setSaving(true);
    try {
      const form = new FormData();
      if (nameInput !== (user?.profile?.name || '')) form.append('name', nameInput || '');
      if (bioInput !== (user?.profile?.bio || '')) form.append('bio', bioInput || '');
      if (disciplineInput !== (user?.profile?.discipline || '')) 
        form.append('discipline', disciplineInput || '');
      if (disciplineOtherInput !== (user?.profile?.discipline_other || ''))
        form.append('discipline_other', disciplineOtherInput || '');
      
      if (locationInput !== (user?.profile?.location || '')) 
        form.append('location', locationInput || '');
      const currentOpenTo = JSON.stringify(user?.profile?.open_to || []);
      if (JSON.stringify(openToInput) !== currentOpenTo) 
        form.append('open_to', JSON.stringify(openToInput));
      if ([...form.keys()].length === 0) { setEditing(false); return; }
      await updateMe(form);
      await refreshMe();
      setEditing(false);
      setDisplayNameError(null);
    } catch (e) {
      console.error(e);
      const backendError = e?.response?.data?.profile?.name?.[0];
      if (backendError) setDisplayNameError(backendError);
      else alert(e?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Avatar must be an image.'); return; }
    if (file.size > 8 * 1024 * 1024) { alert('Avatar too large (max 8MB).'); return; }
    setAvatarUploading(true);
    try {
      const form = new FormData();
      form.append('avatar', file);
      await updateMe(form);
      await refreshMe();
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Failed to upload avatar');
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');
    try {
      await resendVerification(user?.email);
      setResendMessage('✓ Verification email sent! Check your inbox.');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        const retryAfter = err?.response?.data?.retry_after || 60;
        setResendMessage(`Please wait ${retryAfter}s before resending.`);
      } else {
        setResendMessage('Failed to resend. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="p-6">
        <div className="animate-pulse text-field-grey">Loading profile…</div>
      </div>
    );
  }

  return (
    <div className="text-swan max-w-6xl mx-auto p-4 md:p-6 font-poppins pb-12 mt-[4.25rem]">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-center gap-4">
          <Avatar src={user?.profile?.avatar_url} name={displayName} />
          <div>
            <div className="text-xl font-semibold mb-2">{displayName}</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-field-grey">{user?.email}</span>
              <EmailStatusBadge
                emailVerified={user?.email_verified || false}
                email={user?.email}
                size="sm"
                showLabel={false}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!editing ? (
            <>
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center justify-center rounded-md border border-zinc-800 px-3 py-2 text-sm hover:bg-zinc-900"
              >
                Edit profile
              </button>
              <button
                onClick={() => setShowChangeEmail(!showChangeEmail)}
                className="inline-flex items-center justify-center rounded-md border border-mikado-yellow px-3 py-2 text-sm text-mikado-yellow hover:bg-yellow-950/20"
              >
                {showChangeEmail ? 'Hide' : 'Change email'}
              </button>
              {!user?.email_verified && (
                <button
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="inline-flex items-center justify-center rounded-md border border-mikado-yellow px-3 py-2 text-sm text-mikado-yellow hover:bg-yellow-950/20"
                >
                  {resendLoading ? 'Sending...' : 'Resend verification'}
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={onSaveProfile}
                disabled={saving}
                className={classNames(
                  'inline-flex items-center justify-center rounded-md px-3 py-2 text-sm',
                  saving ? 'bg-zinc-700' : 'bg-white text-black hover:opacity-90'
                )}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setNameInput(user?.profile?.name || '');
                  setBioInput(user?.profile?.bio || '');
                  setDisplayNameError(null);
                  setDisciplineInput(user?.profile?.discipline || '');
                  setLocationInput(user?.profile?.location || '');
                  setOpenToInput(user?.profile?.open_to || []);
                  setDisciplineOtherInput(user?.profile?.discipline_other || '');
                }}
                className="inline-flex items-center justify-center rounded-md border border-zinc-800 px-3 py-2 text-sm hover:bg-zinc-900"
              >
                Cancel
              </button>
            </>
          )}
          <button
            onClick={handleLogout}
            className="inline-flex items-center justify-center rounded-md border border-imperial-red text-imperial-red px-3 py-2 text-sm hover:bg-red-950/30"
          >
            Log out
          </button>
        </div>
      </div>

      {/* Resend verification message */}
      {resendMessage && (
        <div className="mt-4 p-3 rounded-md bg-zinc-900/50 border border-zinc-700">
          <p className="text-sm text-mikado-yellow">{resendMessage}</p>
        </div>
      )}

      {/* Change email form */}
      {showChangeEmail && (
        <div className="mt-6">
          <ChangeEmailForm
            currentEmail={user?.email}
            onEmailChanged={() => { refreshMe(); setShowChangeEmail(false); }}
          />
        </div>
      )}

      {/* Editable fields */}
      <div className="mt-6 grid gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wide text-mikado-yellow mb-1">
            Display name
            <span className="text-xs text-field-grey ml-2">(max 2 words, 50 chars)</span>
          </label>
          <input
            type="text"
            value={nameInput}
            disabled={!editing}
            onChange={handleNameChange}
            placeholder="e.g., Larry Jackson, Apollonia"
            className={classNames(
              'w-full rounded-md border bg-transparent px-3 py-2',
              displayNameError && editing
                ? 'border-imperial-red focus:ring-imperial-red'
                : editing
                ? 'border-zinc-700 focus:outline-none focus:ring-1 focus:ring-field-grey'
                : 'border-zinc-900 text-zinc-400'
            )}
          />
          {displayNameError && editing && (
            <p className="text-xs text-imperial-red mt-1">{displayNameError}</p>
          )}
          {!displayNameError && nameInput && editing && (
            <p className="text-xs text-mikado-yellow mt-1">✓ Valid display name</p>
          )}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-mikado-yellow mb-1">Bio</label>
          <textarea
            value={bioInput}
            disabled={!editing}
            onChange={(e) => setBioInput(e.target.value)}
            placeholder="Who are you?"
            rows={3}
            className={classNames(
              'w-full rounded-md border bg-transparent px-3 py-2',
              editing
                ? 'border-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-400'
                : 'border-zinc-900 text-field-grey'
            )}
          />
        </div>

       {/* Discipline */}
        <div>
          <label className="block text-xs uppercase tracking-wide text-mikado-yellow mb-1">
            Discipline
          </label>
          {editing ? (
            <select
              value={disciplineInput}
              onChange={(e) => setDisciplineInput(e.target.value)}
              className="w-full rounded-md border bg-transparent px-3 py-2 border-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-400"
            >
              <option value="">Select your discipline</option>
              <option value="musician">Musician</option>
              <option value="dancer">Dancer</option>
              <option value="photographer">Photographer</option>
              <option value="designer">Designer</option>
              <option value="filmmaker">Filmmaker</option>
              <option value="developer">Developer</option>
              <option value="writer">Writer</option>
              <option value="other">Other</option>
            </select>
          ) : (
            <div className="w-full rounded-md border border-zinc-900 px-3 py-2 text-zinc-400 capitalize">
              {disciplineInput === 'other' && disciplineOtherInput
                ? disciplineOtherInput
                : disciplineInput || '—'}
            </div>
          )}
        </div>

        {/* Discipline Other — only shown in edit mode when Other is selected */}
        {disciplineInput === 'other' && editing && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-imperial-red mb-1">
              Please specify
            </label>
            <input
              type="text"
              value={disciplineOtherInput}
              onChange={(e) => setDisciplineOtherInput(e.target.value)}
              placeholder="e.g. Beatboxer, Muralist, Poet..."
              maxLength={100}
              className="w-full rounded-md border bg-transparent px-3 py-2 border-zinc-700 focus:outline-none focus:ring-1 focus:ring-field-grey"
            />
          </div>
        )}


        {/* Location */}
        <div>
          <label className="block text-xs uppercase tracking-wide text-mikado-yellow mb-1">
            Location
          </label>
          <input
            type="text"
            value={locationInput}
            disabled={!editing}
            onChange={(e) => setLocationInput(e.target.value)}
            placeholder="e.g. London, UK"
            className={classNames(
              'w-full rounded-md border bg-transparent px-3 py-2',
              editing
                ? 'border-zinc-700 focus:outline-none focus:ring-1 focus:ring-field-grey'
                : 'border-zinc-900 text-zinc-400'
            )}
          />
        </div>

        {/* Open To */}
        <div>
          <label className="block text-xs uppercase tracking-wide text-mikado-yellow mb-2">
            Open to
          </label>
          <div className="flex flex-wrap gap-2">
            {['collaborating', 'performing', 'networking', 'mentoring', 'hiring'].map((option) => {
              const active = openToInput.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  disabled={!editing}
                  onClick={() => {
                    if (!editing) return;
                    setOpenToInput((prev) =>
                      prev.includes(option)
                        ? prev.filter((x) => x !== option)
                        : [...prev, option]
                    );
                  }}
                  className={classNames(
                    'px-3 py-1.5 rounded-full text-xs font-medium border transition-all capitalize',
                    active
                      ? 'bg-mikado-yellow/10 border-mikado-yellow text-mikado-yellow'
                      : 'border-zinc-700 text-zinc-500 hover:border-zinc-500',
                    !editing && 'cursor-default opacity-70'
                  )}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>



        <div>
          <label className="block text-xs uppercase tracking-wide text-mikado-yellow mb-1">Avatar</label>
          <input
            type="file"
            accept="image/*"
            onChange={onAvatarPick}
            disabled={avatarUploading || !editing}
            className="text-sm text-field-grey disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {avatarUploading && <p className="text-xs text-field-grey mt-1">Uploading…</p>}
        </div>
      </div>

      {/* Media grid */}
      <div className="mt-10">
        <h2 className="text-lg font-semibold mb-3">Your media</h2>
        {files.length === 0 ? (
          <div className="text-sm text-field-grey">No uploads yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {files.map((f) => {
              const isImage = f.fileType === 'image';
              const isVideo = f.fileType === 'video';
              const submission = fileToSubmissionMap[f.id];
              const comp = submission?.competition_detail;

              // ✅ Always trust competition_url from backend — it uses
              // FRONTEND_URL env var so it's correct on local, staging, and prod.
              // No need for DEV checks here.
              const handleCompetitionClick = (e) => {
                e.stopPropagation();
                if (!comp?.competition_url) return;
                window.location.href = comp.competition_url;
              };

              const endDateLabel = comp?.end_date
                ? `Ends ${new Date(comp.end_date).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}`
                : null;

              const isOpen = comp?.end_date && new Date(comp.end_date) > new Date();

              return (
                <div
                  key={f.id}
                  className="relative group rounded-md overflow-hidden border border-zinc-800 hover:border-mikado-yellow/40 transition-all flex flex-col"
                >
                  {/* Thumbnail — opens submission modal */}
                  <div
                    onClick={() => {
                      if (submission) {
                        openModal('SUBMISSION_PREVIEW', {
                          submission,
                          user: user?.id,
                          onDelete: (id) =>
                            setSubs((prev) => prev.filter((s) => s.id !== id)),
                          onEdit: () => {},
                        });
                      }
                    }}
                    className="cursor-pointer"
                  >
                    {isImage && (
                      <img
                        src={f.url}
                        alt=""
                        className="w-full h-40 object-cover group-hover:opacity-90 transition"
                        loading="lazy"
                      />
                    )}
                    {isVideo && (
                      <video
                        src={f.url}
                        className="w-full h-40 object-cover"
                        muted
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={(e) => { e.currentTarget.currentTime = 0.1; }}
                        onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                        onMouseLeave={(e) => {
                          e.currentTarget.pause();
                          e.currentTarget.currentTime = 0;
                        }}
                      />
                    )}
                    {!isImage && !isVideo && (
                      <div className="w-full h-40 grid place-items-center text-xs text-field-grey">
                        Unsupported
                      </div>
                    )}
                  </div>

                  {/* Competition label — navigates to correct subdomain */}
                  {comp && (
                    <button
                      onClick={handleCompetitionClick}
                      className="w-full text-left px-2 py-1.5 bg-zinc-900/80 border-t border-zinc-800 hover:bg-zinc-800 transition-colors"
                    >
                      <p className="text-xs text-mikado-yellow font-medium truncate leading-snug">
                        {comp.title}
                      </p>
                      <div className="flex items-center justify-between mt-0.5">
                        {endDateLabel && (
                          <p className="text-xs text-field-grey truncate leading-tight">
                            {endDateLabel}
                          </p>
                        )}
                        <span className={`text-xs font-medium shrink-0 ml-1 ${
                          isOpen ? 'text-green-400' : 'text-zinc-500'
                        }`}>
                          {isOpen ? 'Open' : 'Closed'}
                        </span>
                      </div>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}